<template>
  <MiDialog
    class="service-crud-modal"
    :model-value="modelValue"
    width="100%"
    :title="isNewService ? $t('Services.AddService') : $t('Services.EditService')"
    v-bind="$attrs"
    @update:model-value="$emit('update:modelValue', $event)">
    <div v-if="loading.getData" class="service-crud-modal__loading" v-loading="true" />
    <VeeForm v-else class="service-crud-modal__form" as="ElForm">
      <ServiceMainForm
        v-if="!!formData.values"
        :form-data="formData.values"
        @close:modal="$emit('update:modelValue')"
        @action="$emit('action', $event)" />
    </VeeForm>
  </MiDialog>
</template>

<script lang="ts">
export default {
  name: 'ServiceCrudModal',
  inheritAttrs: false,
  customOptions: {},
};
</script>

<script lang="ts" setup>
import axios from 'axios';
import { ElNotification } from 'element-plus';
import { Form as VeeForm } from 'vee-validate';
import { reactive, watch, computed } from 'vue';

// eslint-disable-next-line import/no-internal-modules
import ServiceMainForm from './ServiceMainForm/index.vue';

import {
  ServiceGroupBase,
  ServiceGroupProcedure,
  ServiceGroupProduct,
  ServiceGroupConsultation,
  ServiceGroupConsultationFormData,
  ServiceGroupProcedureFormdata,
  ServiceGroupProductFormData,
} from '@/models/accounting/Service.model';
import { misB2BApi, PriceListDto, PriceListCostDto } from '~shared/api';
import { MiDialog } from '~shared/ui';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    id?: number | null;
  }>(),
  {
    modelValue: false,
    id: null,
  }
);

defineEmits(['update:modelValue', 'action']);

const formData = reactive<{
  values:
    | ServiceGroupConsultationFormData
    | ServiceGroupProcedureFormdata
    | ServiceGroupProductFormData
    | null;
}>({ values: null });

const loading = reactive({
  getData: false,
});

const isNewService = computed(() => !props.id);
let priceLists = reactive<PriceListDto[]>([]);

const getPriceLists = async () => {
  const response = await misB2BApi.priceLists.getList();

  if (response?.data) {
    priceLists = response.data;
  }
};

const getServiceGroupById = async (id: number) => {
  loading.getData = true;

  try {
    const response = await ServiceGroupBase.getItemById(id);

    if (response?.data && !response.data?.price_lists.length) {
      await getPriceLists();
      response.data.price_lists = preparePriceLists(priceLists);
    }

    switch (response?.data.type as ServiceGroupBase['type'] | undefined) {
      case 'analysis':
      case 'procedure':
        formData.values = { ...new ServiceGroupProcedure(response?.data) };
        break;
      case 'product':
        formData.values = { ...new ServiceGroupProduct(response?.data) };
        break;
      case 'consultation':
        formData.values = { ...new ServiceGroupConsultation(response?.data) };
        break;
      default:
        formData.values = { ...new ServiceGroupConsultation(response?.data) };
    }
  } catch (err) {
    ElNotification({
      type: 'error',
      title: axios.isAxiosError(err) ? err.message : String(err),
    });
  } finally {
    loading.getData = false;
  }
};

const preparePriceLists = (data: PriceListDto[]): PriceListCostDto[] => {
  return data.map(({ id, title }) => ({
    id,
    price: null,
    title,
  }));
};

watch(
  () => props.id,
  async (value) => {
    if (value) {
      getServiceGroupById(value);
    } else {
      await getPriceLists();
      formData.values = {
        ...new ServiceGroupConsultation({
          price_lists: preparePriceLists(priceLists),
        } as ServiceGroupConsultationFormData),
      };
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" src="./index.scss" />
